import {
  Add as AddIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  CalendarToday as AgendaIcon,
  People as ClientesIcon,
  AccountBalance as ImpostosIcon,
  Assignment as ObrigacoesIcon,
  // PriorityHigh as PrioridadesIcon,
  Refresh as RecorrenciaIcon,
  Assessment as RelatoriosIcon,
  Business as SetoresIcon,
  Task as TarefasIcon,
  Person as UsuariosIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Collapse,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Snackbar,
  Typography,
  useTheme,
} from "@mui/material";
import React from "react";
import { NavLink } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { clienteService, tarefaService, usuarioService } from "../services/api";
import TarefaModal from "./TarefaModal";

const SideMenu = ({ open, onToggle, isMobile }) => {
  const theme = useTheme();
  const { user } = useAuth();
  const [relatoriosSubmenuOpen, setRelatoriosSubmenuOpen] =
    React.useState(false);

  // Estado do modal de tarefa e formulários
  const [openTaskDialog, setOpenTaskDialog] = React.useState(false);
  const [salvando, setSalvando] = React.useState(false);
  const [clientes, setClientes] = React.useState([]);
  const [usuarios, setUsuarios] = React.useState([]);
  const [formData, setFormData] = React.useState({
    cliente_id: "",
    descricao: "",
    data_vencimento: new Date(),
    hora_vencimento: new Date(),
    status: "pendente",
    responsavel_id: "",
    priorizar: false,
    comentarios: "",
  });
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Carregar clientes/usuarios na montagem (dados necessários para o modal)
  React.useEffect(() => {
    const carregar = async () => {
      try {
        const [respClientes, respUsuarios] = await Promise.all([
          clienteService.getAll({ ativo: true }),
          usuarioService.getAll(),
        ]);
        setClientes(respClientes.clientes || respClientes.data || []);
        setUsuarios(respUsuarios.usuarios || respUsuarios.data || []);
      } catch (e) {
        // Silenciar erros aqui para não poluir o menu; modal continuará abrindo e pode falhar na validação
        // Opcionalmente, poderíamos mostrar um snackbar global
      }
    };
    carregar();
  }, []);

  const abrirTaskDialog = () => {
    setFormData({
      cliente_id: "",
      descricao: "",
      data_vencimento: new Date(),
      hora_vencimento: new Date(),
      status: "pendente",
      responsavel_id: "",
      priorizar: false,
      comentarios: "",
    });
    setOpenTaskDialog(true);
  };

  const fecharTaskDialog = () => {
    setOpenTaskDialog(false);
  };

  const handleSubmitTask = async (e) => {
    e.preventDefault();
    if (salvando) return;

    if (
      !formData.cliente_id ||
      !formData.descricao ||
      !formData.data_vencimento ||
      !formData.hora_vencimento
    ) {
      // Sem snackbar local aqui; validação básica
      return;
    }

    try {
      setSalvando(true);
      const formatarDataLocal = (data) => {
        const ano = data.getFullYear();
        const mes = String(data.getMonth() + 1).padStart(2, "0");
        const dia = String(data.getDate()).padStart(2, "0");
        return `${ano}-${mes}-${dia}`;
      };

      const dadosEnvio = {
        ...formData,
        data_vencimento: formatarDataLocal(formData.data_vencimento),
        hora_vencimento: formData.hora_vencimento.toTimeString().slice(0, 5),
      };

      await tarefaService.create(dadosEnvio);
      setSnackbar({
        open: true,
        message: "Tarefa criada com sucesso",
        severity: "success",
      });

      fecharTaskDialog();
      // Se o usuário estiver em /agenda ou /tarefas, recarregar a página atual
      const path = window.location.pathname || "";
      if (path.startsWith("/agenda") || path.startsWith("/tarefas")) {
        window.location.reload();
      }
    } catch (error) {
      // Silenciar erro localmente (sem snackbar no menu)
    } finally {
      setSalvando(false);
    }
  };

  const fecharSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Função para renderizar texto com números destacados
  const renderTextWithHighlightedNumbers = (text) => {
    // Regex para encontrar números no final do texto (como ①, ②, ③, ④)
    const parts = text.split(/([①②③④⑤⑥⑦⑧⑨⑩])/);

    return (
      <span>
        {parts.map((part, index) => {
          // Se é um número, renderiza com fonte maior
          if (/[①②③④⑤⑥⑦⑧⑨⑩]/.test(part)) {
            return (
              <span
                key={index}
                style={{
                  fontSize: "1rem",
                  fontWeight: 100,
                }}
              >
                {part}
              </span>
            );
          }
          return part;
        })}
      </span>
    );
  };

  // Calcular iniciais do usuário
  const getInitials = (name) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase())
      .slice(0, 2)
      .join("");
  };

  const menuItems = [
    // {
    //   text: "Dashboard",
    //   icon: <DashboardIcon />,
    //   path: "/dashboard",
    //   section: "main",
    // },
    { text: "Agenda", icon: <AgendaIcon />, path: "/agenda", section: "main" },
    {
      text: "Tarefas",
      icon: <TarefasIcon />,
      path: "/tarefas",
      section: "main",
    },
    {
      text: "Obrigações",
      icon: <ObrigacoesIcon />,
      path: "/obrigacoes",
      section: "main",
    },
    {
      text: "Recorrências",
      icon: <RecorrenciaIcon />,
      path: "/recorrencias",
      section: "main",
    },
    {
      text: "Clientes ④",
      icon: <ClientesIcon />,
      path: "/clientes",
      section: "main",
    },
    {
      text: "Tipos de obrigações ③",
      icon: <ImpostosIcon />,
      path: "/impostos",
      section: "main",
    },
    {
      text: "Usuários ②",
      icon: <UsuariosIcon />,
      path: "/usuarios",
      section: "main",
      onlyFor: (user) => user && user.role === "admin",
    },
    {
      text: "Setores ①",
      icon: <SetoresIcon />,
      path: "/setores",
      section: "main",
      onlyFor: (user) => user && user.role === "admin",
    },
    // {
    //   text: "Prioridades",
    //   icon: <PrioridadesIcon />,
    //   path: "/prioridades",
    //   section: "main",
    // },
    {
      text: "Relatórios",
      icon: <RelatoriosIcon />,
      path: "/relatorios",
      section: "main",
      hasSubmenu: true,
      submenuItems: [
        {
          text: "Obrigações enviadas",
          path: "/relatorios/obrigacoes-enviadas",
          icon: <ObrigacoesIcon sx={{ fontSize: "1rem", color: "white" }} />,
        },
        // Futuros relatórios podem ser adicionados aqui
        // {
        //   text: "Tarefas concluídas",
        //   path: "/relatorios/tarefas-concluidas",
        // },
      ],
    },
    // {
    //   text: "Assinatura",
    //   icon: <AssinaturaIcon />,
    //   path: "/assinatura",
    //   section: "main",
    // },
  ];

  // Adiciona o link do painel de admin apenas se o usuário for super admin
  if (user && user.is_super_admin) {
    menuItems.push({
      text: "Painel Admin",
      icon: <AdminPanelSettingsIcon />,
      path: "/admin",
      section: "admin", // Pode ser útil para separação futura
    });
  }

  // const generalItems = [
  //   {
  //     text: "Notificações",
  //     icon: <NotificationsIcon />,
  //     path: "/notificacoes",
  //   },
  //   { text: "Progresso", icon: <ProgressIcon />, path: "/progresso" },
  //   { text: "Twitter feed", icon: <TwitterIcon />, path: "/twitter" },
  //   { text: "Lista de tarefas", icon: <TodoIcon />, path: "/todo" },
  // ];

  const drawerContent = (
    <Box
      sx={{
        width: open ? 280 : 80,
        minWidth: open ? 280 : 80,
        maxWidth: open ? 280 : 80,
        minHeight: "100vh",
        bgcolor: "#4a5568",
        color: "white",
        transition: theme.transitions.create(["width"], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        flexShrink: 0,
      }}
    >
      {/* Logo/Header */}
      <Box
        sx={{
          p: 2,
          textAlign: "center",
          borderBottom: "1px solid rgba(255,255,255,0.1)",
          minHeight: 64,
          display: "flex",
          alignItems: "center",
          justifyContent: open ? "flex-start" : "center",
          flexShrink: 0,
        }}
      >
        {open ? (
          <Typography
            variant="h6"
            sx={{
              fontWeight: "bold",
              color: "white",
              fontSize: "1.1rem",
            }}
          >
            Olha o prazo!
          </Typography>
        ) : (
          <Typography
            variant="h6"
            sx={{
              fontWeight: "bold",
              color: "white",
              fontSize: "1.2rem",
            }}
          >
            rD
          </Typography>
        )}
      </Box>

      {/* User Info */}
      {open && (
        <Box
          sx={{
            p: 2,
            borderBottom: "1px solid rgba(255,255,255,0.1)",
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              bgcolor: "#2d3748",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundImage:
                "linear-gradient(135deg,rgb(85, 122, 141) 0%,rgb(39, 44, 154) 100%)",
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ color: "white", fontWeight: "bold" }}
            >
              {getInitials(user?.nome)}
            </Typography>
          </Box>
          <Box>
            <Typography
              variant="subtitle2"
              sx={{ color: "white", fontWeight: 500 }}
            >
              Olá, {user?.nome || "Usuário"}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Scrollable content area */}
      <Box sx={{ flexGrow: 1, overflow: "auto" }}>
        {/* Main Navigation */}
        <List sx={{ pt: 1 }}>
          {menuItems
            .filter((item) => !item.onlyFor || item.onlyFor(user))
            .map((item) => (
              <React.Fragment key={item.text}>
                <ListItem disablePadding>
                  <ListItemButton
                    component={item.hasSubmenu ? "div" : NavLink}
                    to={item.hasSubmenu ? undefined : item.path}
                    onMouseEnter={() =>
                      item.hasSubmenu && setRelatoriosSubmenuOpen(true)
                    }
                    onMouseLeave={() =>
                      item.hasSubmenu && setRelatoriosSubmenuOpen(false)
                    }
                    onClick={() =>
                      !item.hasSubmenu && setRelatoriosSubmenuOpen(false)
                    }
                    sx={{
                      minHeight: 48,
                      px: 2.5,
                      justifyContent: open ? "initial" : "center",
                      color: "rgba(255,255,255,0.7)",
                      "&:hover": {
                        bgcolor: "rgba(255,255,255,0.1)",
                        color: "white",
                      },
                      "&.active": {
                        bgcolor: "rgba(255,255,255,0.15)",
                        color: "white",
                        borderRight: "3px solid #48bb78",
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: open ? 3 : "auto",
                        justifyContent: "center",
                        color: "inherit",
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    {open && (
                      <ListItemText
                        primary={renderTextWithHighlightedNumbers(item.text)}
                        primaryTypographyProps={{
                          fontSize: "0.875rem",
                          fontWeight: 500,
                        }}
                      />
                    )}
                  </ListItemButton>
                </ListItem>

                {/* Submenu de Relatórios */}
                {item.hasSubmenu && open && (
                  <Collapse
                    in={relatoriosSubmenuOpen}
                    timeout="auto"
                    unmountOnExit
                  >
                    <Box
                      onMouseEnter={() => setRelatoriosSubmenuOpen(true)}
                      onMouseLeave={() => setRelatoriosSubmenuOpen(false)}
                    >
                      <List component="div" disablePadding>
                        {item.submenuItems.map((subItem) => (
                          <ListItem key={subItem.text} disablePadding>
                            <ListItemButton
                              component={NavLink}
                              to={subItem.path}
                              sx={{
                                pl: 6,
                                py: 1,
                                color: "rgba(255,255,255,0.6)",
                                fontSize: "0.8rem",
                                "&:hover": {
                                  bgcolor: "rgba(255,255,255,0.08)",
                                  color: "white",
                                },
                                "&.active": {
                                  bgcolor: "rgba(255,255,255,0.12)",
                                  color: "white",
                                  borderRight: "2px solid #48bb78",
                                },
                              }}
                            >
                              {subItem.icon && (
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                  {subItem.icon}
                                </ListItemIcon>
                              )}
                              <ListItemText
                                primary={subItem.text}
                                primaryTypographyProps={{
                                  fontSize: "0.8rem",
                                  fontWeight: 400,
                                }}
                              />
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  </Collapse>
                )}
              </React.Fragment>
            ))}
        </List>

        {/* General Section */}
        {/* {open && (
          <>
            <Divider
              sx={{ mx: 2, my: 1, borderColor: "rgba(255,255,255,0.1)" }}
            />
            <Box sx={{ px: 2, py: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  color: "#a0aec0",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                }}
              >
                Geral
              </Typography>
            </Box>
            <List dense>
              {generalItems.map((item) => (
                <ListItem key={item.text} disablePadding>
                  <ListItemButton
                    component={NavLink}
                    to={item.path}
                    sx={{
                      minHeight: 40,
                      px: 2.5,
                      color: "rgba(255,255,255,0.7)",
                      "&:hover": {
                        bgcolor: "rgba(255,255,255,0.1)",
                        color: "white",
                      },
                      "&.active": {
                        bgcolor: "rgba(255,255,255,0.15)",
                        color: "white",
                        borderRight: "3px solid #48bb78",
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: 3,
                        justifyContent: "center",
                        color: "inherit",
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{
                        fontSize: "0.8rem",
                        fontWeight: 400,
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </>
        )} */}
      </Box>

      {/* Rodapé com ação rápida: Nova tarefa */}
      <Box
        sx={{
          p: 1.5,
          borderTop: "1px solid rgba(255,255,255,0.1)",
          bgcolor: "rgba(0,0,0,0.05)",
        }}
      >
        <Button
          onClick={abrirTaskDialog}
          variant="contained"
          startIcon={open ? <AddIcon /> : null}
          sx={{
            width: "100%",
            minHeight: 40,
            justifyContent: "center",
            px: 2,
            backgroundColor: "#1976d2",
          }}
        >
          {open ? "Nova tarefa" : <AddIcon />}
        </Button>
      </Box>

      {/* Modal de tarefa reutilizável */}
      <TarefaModal
        open={openTaskDialog}
        onClose={fecharTaskDialog}
        onSubmit={handleSubmitTask}
        formData={formData}
        setFormData={setFormData}
        clientes={clientes}
        usuarios={usuarios}
        salvando={salvando}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={fecharSnackbar}
      >
        <Alert
          onClose={fecharSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );

  // Para mobile, continua usando Drawer
  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={open}
        onClose={onToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          "& .MuiDrawer-paper": {
            width: 280,
            boxSizing: "border-box",
            border: "none",
          },
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  // Para desktop, usa um Box normal dentro do flex container
  return drawerContent;
};

export default SideMenu;
